/**
 * @deprecated This file is maintained as a static fallback only.
 *
 * Tags are now managed dynamically via Firestore at: siteContent/tags
 * All components should use the useTags() composable from:
 *   @/composables/useTags.js
 *
 * The Admin Dashboard provides a UI for managing tags at /admin
 *
 * This file serves as:
 * 1. Fallback if Firestore tags document doesn't exist
 * 2. Source for initial seeding of Firestore tags
 *
 * DO NOT add new tags here - use the Admin Dashboard instead.
 */

// Centralized tag configuration for PALLAS
// This ensures all components use the same tag definitions and theming
// Tags are sorted alphabetically within each category

export const PREDEFINED_TAGS = [
  // === ART TAGS (sorted alphabetically) ===

  // Animation & Motion
  { name: "2D Animation", theme: "art" },
  { name: "3D Animation", theme: "art" },
  { name: "Animation", theme: "art" },
  { name: "Character Animation", theme: "art" },
  { name: "Kinetic Typography", theme: "art" },
  { name: "Motion Graphics", theme: "art" },
  { name: "Stop Motion", theme: "art" },

  // Digital Art & Design
  { name: "3D Sculpting", theme: "art" },
  { name: "Concept Art", theme: "art" },
  { name: "Digital 2D", theme: "art" },
  { name: "Digital 3D", theme: "art" },
  { name: "Digital Collage", theme: "art" },
  { name: "Digital Illustration", theme: "art" },
  { name: "Matte Painting", theme: "art" },
  { name: "Photo Manipulation", theme: "art" },
  { name: "Vector Graphics", theme: "art" },

  // Graphic Design & Publishing
  { name: "Editorial Illustration", theme: "art" },
  { name: "Graphic Design", theme: "art" },
  { name: "Icon Design", theme: "art" },
  { name: "Logo Design", theme: "art" },
  { name: "Poster Design", theme: "art" },
  { name: "Presentation Design", theme: "art" },
  { name: "Publication Design", theme: "art" },
  { name: "Storyboarding", theme: "art" },
  { name: "Typography", theme: "art" },

  // Subject Matter & Focus Areas
  { name: "Abstract", theme: "art" },
  { name: "Animals & Wildlife", theme: "art" },
  { name: "Board & Card Game Art", theme: "art" },
  { name: "Book Illustration", theme: "art" },
  { name: "Character Design", theme: "art" },
  { name: "Children's Art", theme: "art" },
  { name: "Comic Art", theme: "art" },
  { name: "Environmental Concept Art & Design", theme: "art" },
  { name: "Fashion & Costume Design", theme: "art" },
  { name: "Game Art", theme: "art" },
  { name: "Illustration", theme: "art" },
  { name: "Journal Cover Art", theme: "art" },
  { name: "Pixel and Voxel Art", theme: "art" },
  { name: "Portraits", theme: "art" },
  { name: "Realism", theme: "art" },
  { name: "Science Fiction", theme: "art" },
  { name: "Sketches", theme: "art" },
  { name: "Vehicles", theme: "art" },

  // Traditional Medium/Technique
  { name: "Calligraphy", theme: "art" },
  { name: "Ceramics", theme: "art" },
  { name: "Collage", theme: "art" },
  { name: "Drawing", theme: "art" },
  { name: "Lettering", theme: "art" },
  { name: "Mixed Media", theme: "art" },
  { name: "Murals", theme: "art" },
  { name: "Painting", theme: "art" },
  { name: "Pottery", theme: "art" },
  { name: "Printmaking", theme: "art" },
  { name: "Sculpture", theme: "art" },
  { name: "Traditional Dry Media", theme: "art" },
  { name: "Traditional Ink", theme: "art" },
  { name: "Traditional Paint", theme: "art" },
  { name: "Traditional Sculpture", theme: "art" },

  // Traditional Crafts & Materials
  { name: "Fiber Arts", theme: "art" },
  { name: "Glass Work", theme: "art" },
  { name: "Metalworking", theme: "art" },
  { name: "Textile Arts", theme: "art" },
  { name: "Woodworking", theme: "art" },

  // Audio & Visual Arts
  { name: "Environmental Art", theme: "art" },
  { name: "Land Art", theme: "art" },
  { name: "Music Composition", theme: "art" },
  { name: "Performance Art", theme: "art" },

  // === SCIENCE TAGS (sorted alphabetically) ===

  // Astronomy & Space Sciences
  { name: "Astrobiology", theme: "science" },
  { name: "Astronomy", theme: "science" },
  { name: "Astrophysics", theme: "science" },
  { name: "Planetary Science", theme: "science" },

  // Biological Sciences
  { name: "Biochemistry", theme: "science" },
  { name: "Bioinformatics", theme: "science" },
  { name: "Biology", theme: "science" },
  { name: "Biophysics", theme: "science" },
  { name: "Computational Biology", theme: "science" },
  { name: "Ecology", theme: "science" },
  { name: "Genetics", theme: "science" },
  { name: "Genomics", theme: "science" },
  { name: "Immunology", theme: "science" },
  { name: "Neuroscience", theme: "science" },
  { name: "Pathology", theme: "science" },
  { name: "Synthetic Biology", theme: "science" },

  // Chemistry & Materials
  { name: "Chemistry", theme: "science" },
  { name: "Materials Science", theme: "science" },
  { name: "Nanotechnology", theme: "science" },

  // Computer & Data Sciences
  { name: "Artificial Intelligence", theme: "science" },
  { name: "Computer Science", theme: "science" },
  { name: "Cybersecurity", theme: "science" },
  { name: "Data Science", theme: "science" },
  { name: "Information Science", theme: "science" },
  { name: "Machine Learning", theme: "science" },
  { name: "Quantum Computing", theme: "science" },

  // Earth & Environmental Sciences
  { name: "Climatology", theme: "science" },
  { name: "Earth Sciences", theme: "science" },
  { name: "Environmental Science", theme: "science" },
  { name: "Geography", theme: "science" },
  { name: "Geology", theme: "science" },
  { name: "Meteorology", theme: "science" },
  { name: "Oceanography", theme: "science" },
  { name: "Paleontology", theme: "science" },
  { name: "Sustainability Science", theme: "science" },

  // Engineering
  { name: "Aerospace Engineering", theme: "science" },
  { name: "Biomedical Engineering", theme: "science" },
  { name: "Chemical Engineering", theme: "science" },
  { name: "Civil Engineering", theme: "science" },
  { name: "Electrical Engineering", theme: "science" },
  { name: "Mechanical Engineering", theme: "science" },
  { name: "Robotics", theme: "science" },

  // Health & Medical Sciences
  { name: "Epidemiology", theme: "science" },
  { name: "Medicine", theme: "science" },
  { name: "Nutrition Science", theme: "science" },
  { name: "Pharmacology", theme: "science" },
  { name: "Public Health", theme: "science" },
  { name: "Sports Science", theme: "science" },

  // Mathematics & Statistics
  { name: "Applied Mathematics", theme: "science" },
  { name: "Pure Mathematics", theme: "science" },
  { name: "Statistics", theme: "science" },

  // Physics
  { name: "Physics", theme: "science" },

  // Social & Behavioral Sciences
  { name: "Archaeology", theme: "science" },
  { name: "Cognitive Science", theme: "science" },
  { name: "Linguistics", theme: "science" },
  { name: "Psychology", theme: "science" },

  // Interdisciplinary & Applied Sciences
  { name: "Agricultural Science", theme: "science" },
  { name: "Complex Systems", theme: "science" },
  { name: "Forensic Science", theme: "science" },
  { name: "Network Science", theme: "science" },
  { name: "Science and Technology Studies", theme: "science" },
  { name: "Science Communication", theme: "science" },
  { name: "Veterinary Science", theme: "science" }
];

// Tag theme classification arrays (sorted alphabetically)
export const ART_TAGS = [
  '2d animation', '3d animation', '3d sculpting', 'abstract', 'animation',
  'animals & wildlife', 'board & card game art', 'book illustration', 'calligraphy',
  'ceramics', 'character animation', 'character design', 'children\'s art', 'collage',
  'comic art', 'concept art', 'digital 2d', 'digital 3d', 'digital collage',
  'digital illustration', 'drawing', 'editorial illustration', 'environmental art',
  'environmental concept art & design', 'fashion & costume design', 'fiber arts',
  'game art', 'glass work', 'graphic design', 'icon design', 'illustration',
  'journal cover art', 'kinetic typography', 'land art', 'lettering', 'logo design',
  'matte painting', 'metalworking', 'mixed media', 'motion graphics', 'murals',
  'music composition', 'painting', 'performance art', 'photo manipulation',
  'pixel and voxel art', 'portraits', 'poster design', 'pottery', 'presentation design',
  'printmaking', 'publication design', 'realism', 'science fiction', 'sculpture',
  'sketches', 'stop motion', 'storyboarding', 'textile arts', 'traditional dry media',
  'traditional ink', 'traditional paint', 'traditional sculpture', 'typography',
  'vector graphics', 'vehicles', 'woodworking'
];

export const SCIENCE_TAGS = [
  'aerospace engineering', 'agricultural science', 'applied mathematics',
  'archaeology', 'artificial intelligence', 'astrobiology', 'astronomy', 'astrophysics',
  'biochemistry', 'bioinformatics', 'biology', 'biomedical engineering', 'biophysics',
  'chemical engineering', 'chemistry', 'civil engineering', 'climatology',
  'cognitive science', 'complex systems', 'computational biology', 'computer science',
  'cybersecurity', 'data science', 'earth sciences', 'ecology', 'electrical engineering',
  'environmental science', 'epidemiology', 'forensic science', 'genetics', 'genomics',
  'geography', 'geology', 'immunology', 'information science', 'linguistics',
  'machine learning', 'materials science', 'mechanical engineering', 'medicine',
  'meteorology', 'nanotechnology', 'network science', 'neuroscience', 'nutrition science',
  'oceanography', 'paleontology', 'pathology', 'pharmacology', 'physics',
  'planetary science', 'psychology', 'public health', 'pure mathematics',
  'quantum computing', 'robotics', 'science and technology studies', 'science communication',
  'sports science', 'statistics', 'sustainability science', 'synthetic biology',
  'veterinary science'
];

/**
 * Determines the theme for a given tag name
 * @param {string} tagName - The tag name to classify
 * @returns {string} - 'art', 'science', or 'neutral'
 */
export function getTagTheme(tagName) {
  const lowerTagName = tagName.toLowerCase();

  if (ART_TAGS.includes(lowerTagName)) return 'art';
  if (SCIENCE_TAGS.includes(lowerTagName)) return 'science';

  return 'art';
}
